import React from "react";
import { IconExternalLink, IconTrash, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getCurrentReferenceUnitValue } from "features/MappingEditor/utils";
import clsx from "clsx";
import Tooltip from "components/Tooltip";
import TextInput from "components/TextInput";
import Badge from "components/Badge";
import EPDSearchModal from "features/EPDSearchModal";
import MaterialSearch from "features/MaterialSearch";
import SearchField from "../components/SearchField";
import EPDOverviewPopup from "./EPDOverviewPopup";
import { BPEStore, EPDRowProps } from "../ProductEditorStore";
import { observer } from "mobx-react-lite";
import { IconBubbleTooltip, formatFloatNumber } from "utils";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { ExclamationCircleIcon } from "@heroicons/react/solid";

const EPDItem = (props: {
  showLabels?: boolean;
  row: EPDRowProps;
  groupIndex: number;
  rowIndex: number;
  className?: string;
}) => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const labelStyle =
    "text-xs sm:text-sm mb-3 flex items-center text-gray-700 h-6";
  const [openEPDSearch, setOpenEPDSearch] = React.useState(!props.row.epd?.id);
  const [openMaterialSearch, setOpenMaterialSearch] = React.useState(false);
  const [openEPDPrievew, setOpenEPDPreview] = React.useState(false);
  const currentGroup = BPEStore.data.groups?.[props.groupIndex];
  const currentRow = currentGroup?.epds?.[props.rowIndex];

  const itemReferenceUnit = getCurrentReferenceUnitValue(
    currentRow?.epd?.reference_unit.toUpperCase()
  );

  const removeSelectedEPD = async () => {
    BPEStore.onChangeEPDGroup(
      "epd",
      undefined,
      props.groupIndex,
      props.rowIndex
    );
    BPEStore.checkEpdValidationErrors();
    onChangeFactor("factor", undefined);
    await BPEStore.checkEPDSpecifications(props.groupIndex);
  };

  const removeSelectedMaterial = () => {
    BPEStore.onChangeEPDGroup(
      "material",
      undefined,
      props.groupIndex,
      props.rowIndex
    );
    BPEStore.checkMaterialValidationErrors();
  };

  const onChangeFactor = (
    name: string | undefined,
    value: string | undefined
  ) => {
    BPEStore.removeFactorErrors(props.groupIndex, props.rowIndex);
    BPEStore.onChangeEPDGroup(
      name as string,
      value,
      props.groupIndex,
      props.rowIndex
    );
  };

  function percentage() {
    return (
      <div className="col-span-2">
        {props.showLabels ? (
          <label className={labelStyle}>
            {t("productEditor.percentage")}
            <Tooltip
              content={t("productEditor.percentageTooltip", {
                ref_unit: BPEStore.getValueFromObject("reference_unit", isEng),
              })}
              iconClassName="text-gray-500"
            />
          </label>
        ) : null}
        <div className="h-9 items-center flex text-sm font-medium text-gray-500">
          {currentRow?.percentage ? (
            `${formatFloatNumber(currentRow?.percentage * 100)}%`
          ) : (
            <Tooltip
              content={t("productEditor.noPercentageAlert")}
              iconClassName="text-gray-500"
              icon={<ExclamationCircleIcon className="ml-1 w-4 min-w-[16px]" />}
            />
          )}
        </div>
      </div>
    );
  }

  function referenceUnit() {
    return (
      <div className="col-span-3">
        {props.showLabels ? (
          <label className={labelStyle}>
            {`${t("productEditor.factor")}*`}
            <Tooltip
              content={t("productEditor.factorTooltip")}
              iconClassName="text-gray-500"
            />
          </label>
        ) : null}
        <TextInput
          unit={`${
            isEng
              ? itemReferenceUnit?.name ?? "-"
              : itemReferenceUnit?.name_de ?? "-"
          }/${BPEStore.getValueFromObject("reference_unit", isEng)}`}
          noMargin
          min={0}
          placeholder="0.01"
          name="factor"
          handleChange={(value) => onChangeFactor("factor", value)}
          value={currentRow.factor ?? ""}
          type="number"
          hasError={
            BPEStore.hasFactorValidationError(
              props.groupIndex,
              props.rowIndex
            ) || Number(currentRow.factor) === 0
          }
          className="rounded-md"
        />
      </div>
    );
  }

  function epdObject() {
    return (
      <div className="col-span-3">
        {props.showLabels ? (
          <label className={labelStyle}>
            EPD*
            <Tooltip
              content={t("productEditor.epdTooltip")}
              iconClassName="text-gray-500"
            />
          </label>
        ) : null}
        {currentRow?.epd?.name_de ?? currentRow?.epd?.name_en ? (
          <Badge type="auto" className="mt-1">
            <>
              <a
                href={currentRow.epd?.url as string}
                target={"_blank"}
                rel="noreferrer"
              >
                <IconExternalLink
                  className="mr-1 text-indigo-700 cursor-pointer"
                  width={18}
                  stroke={3}
                />
              </a>
              <Tooltip
                truncate
                iconClassName="text-gray-500 max-w-[100px]"
                className="max-w-full"
              >
                <span onClick={() => setOpenEPDPreview(true)}>
                  {currentRow.epd?.name_de ??
                    (currentRow.epd?.name_en as string)}
                </span>
              </Tooltip>
              <IconX
                className="text-indigo-700 hover:text-indigo-500 ml-1 cursor-pointer min-w-4"
                width={18}
                stroke={3}
                onClick={removeSelectedEPD}
              />
            </>
          </Badge>
        ) : (
          <SearchField
            placeholder={t("productEditor.searchForAnEPD")}
            onClick={() => setOpenEPDSearch(true)}
            hasError={BPEStore.hasEpdValidationError(
              props.groupIndex,
              props.rowIndex
            )}
          />
        )}
      </div>
    );
  }

  function materialObject() {
    return (
      <div className="col-span-3">
        {props.showLabels ? (
          <label className={labelStyle}>
            Material*
            <Tooltip
              content={t("productEditor.materialTooltip")}
              iconClassName="text-gray-500"
            />
          </label>
        ) : null}
        {currentRow?.material ? (
          <Badge type="auto" className="mt-1">
            <>
              <Tooltip
                truncate
                iconClassName="text-gray-500"
                className="!cursor-auto"
              >
                {currentRow?.material?.name}
              </Tooltip>
              <IconX
                className="text-indigo-700 hover:text-indigo-500 ml-1 cursor-pointer cursor- min-w-4"
                width={18}
                onClick={removeSelectedMaterial}
                stroke={3}
              />
            </>
          </Badge>
        ) : (
          <SearchField
            placeholder={t("productEditor.searchForAnMaterial")}
            onClick={() => setOpenMaterialSearch(true)}
            hasError={BPEStore.hasMaterialValidationError(
              props.groupIndex,
              props.rowIndex
            )}
          />
        )}
      </div>
    );
  }

  function getTooltipText(): React.ReactNode {
    const productRefUnit = BPEStore.getValueFromObject("reference_unit", isEng);
    const epdRefUnit = REFERENCE_UNITS.find(
      (unit) => unit.id === currentRow.epd?.reference_unit
    );

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: t("productEditor.epdRowTooltip", {
            ref_unit_bp: productRefUnit,
            product_name: BPEStore.data.name,
            factor: currentRow?.factor,
            ref_unit_epd: isEng ? epdRefUnit?.name : epdRefUnit?.name_de,
            epd_name: currentRow?.epd?.name_de,
          }),
        }}
      ></span>
    );
  }

  function rowIcons() {
    return (
      <div className="col-span-1">
        {props.showLabels ? (
          <div className={`${labelStyle} min-h-5`}></div>
        ) : null}
        <div className="flex items-center justify-end h-[36px]">
          <Tooltip
            content={getTooltipText()}
            icon={IconBubbleTooltip}
            className="mr-3"
          />

          {currentGroup.epds.length > 1 ? (
            <IconTrash
              className="text-red-700 cursor-pointer hover:text-red-900 transition min-w-5"
              width={24}
              onClick={() =>
                BPEStore.removeEpdRow(props.rowIndex, props.groupIndex)
              }
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={clsx("grid grid-cols-12 gap-4 mb-4", props.className)}>
      {percentage()}
      {referenceUnit()}
      {epdObject()}
      {materialObject()}
      {rowIcons()}
      {openEPDSearch && (
        <EPDSearchModal
          open={openEPDSearch}
          close={() => setOpenEPDSearch(false)}
          datasetIndex={props.groupIndex}
          rowIndex={props.rowIndex}
        />
      )}
      {openMaterialSearch ? (
        <MaterialSearch
          open={openMaterialSearch}
          close={() => setOpenMaterialSearch(false)}
          datasetIndex={props.groupIndex}
          rowIndex={props.rowIndex}
        />
      ) : null}
      <EPDOverviewPopup
        epdID={currentRow.epd?.id}
        open={openEPDPrievew}
        close={() => setOpenEPDPreview(false)}
      />
    </div>
  );
};

export default observer(EPDItem);
